import { useEffect, useMemo } from 'react';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { usePersistentTableState } from '@/shared/hooks/usePersistentTableState';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { normalizeDataTableFilters } from '@/shared/lib/dataTableFilters';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { RequestEmpty, RequestErrorBanner } from '@/shared/ui/RequestState';
import { EntityActionPanel } from '@/shared/ui/EntityActionPanel/EntityActionPanel';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestStatus } from '@/shared/model/requestStatus';
import { CANCELLATION_COLUMNS } from '@/entities/OrderCancellationAnalytics/config/table.config';
import { CANCELLATION_TABLE_DEFAULTS } from '@/entities/OrderCancellationAnalytics/config/options.config';
import { cancellationFiltersToRequest } from '@/entities/OrderCancellationAnalytics/mappers';
import { useOrderCancellationAnalytics } from '@/entities/OrderCancellationAnalytics/model/store';
import classes from '@/styles/common.module.scss';

/** Показывает отменённые заказы с причинами, инициаторами и фильтром периода */
export const OrderCancellationAnalyticsPage = () => {
    const canView = useCanAccessPermission('admin.order-cancellation-analytics.view');
    const table = usePersistentTableState(CANCELLATION_TABLE_DEFAULTS);
    const { filters, first, rows, sortField, sortOrder } = table;
    const { orders, allCount, status, error, fetchList, clear } = useOrderCancellationAnalytics();

    const request = useMemo(() => cancellationFiltersToRequest({
        filters,
        take: rows,
        skip: first,
        sortField,
        sortOrder,
    }), [filters, rows, first, sortField, sortOrder]);

    const debounced = useDebouncedValue(request, 300);

    useEffect(() => {
        if (canView) void fetchList(debounced);
    }, [canView, fetchList, debounced]);

    useEffect(() => clear, [clear]);

    if (!canView) return <RequestEmpty message="Нет доступа к аналитике отменённых заказов" />;

    return <main className={classes.listPage}>
        <EntityActionPanel
            name="Аналитика отменённых заказов"
            buttons={<ActionButton
                label="Обновить"
                icon="pi pi-refresh"
                loading={status === RequestStatus.Loading}
                onClick={() => void fetchList(request)}
            />}
        />
        <RequestErrorBanner
            status={status}
            error={error}
            fallbackMessage="Не удалось загрузить отменённые заказы"
            onRetry={() => fetchList(request)}
        />
        <DataTable
            className={classes.listTable}
            data={orders}
            columns={CANCELLATION_COLUMNS}
            loading={status === RequestStatus.Loading}
            filters={filters}
            onFilterChange={value => table.setFilters(normalizeDataTableFilters(value))}
            onResetFilters={table.reset}
            paginator
            lazy
            first={first}
            rows={rows}
            totalRecords={allCount}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={event => table.setPage(event.first, event.rows)}
            sortField={sortField}
            sortOrder={sortOrder}
            removableSort={false}
            onSort={event => table.setSort(event.sortField ?? 'cancelledAt', event.sortOrder ?? -1)}
            emptyMessage={status === RequestStatus.Error
                ? 'Ошибка загрузки данных'
                : 'Отменённых заказов нет'
            }
        />
    </main>;
};
